import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileArchive, FileSpreadsheet, Loader2, CheckCircle2, ArrowDownRight, ArrowUpRight, History } from "lucide-react";
import { toast } from "sonner";

const Card = ({ icon: Icon, tone, title, desc, accept, multiple, onUpload, testid, result }) => {
  const ref = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!files.length) {
      toast.error("Lütfen dosya seçin");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await onUpload(fd);
      setFiles([]);
      if (ref.current) ref.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-6">
      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${tone}`}>
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mt-4 font-display text-lg font-medium">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>

      <label
        className="mt-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-secondary/30 px-4 py-6 text-center transition-colors hover:border-primary/50 hover:bg-secondary/50"
        data-testid={`${testid}-dropzone`}
      >
        <UploadCloud className="h-6 w-6 text-muted-foreground" />
        <span className="mt-2 text-xs text-muted-foreground">
          {files.length ? `${files.length} dosya seçildi` : "Dosya seçmek için tıklayın"}
        </span>
        <input
          ref={ref}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          data-testid={`${testid}-input`}
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
      </label>

      <Button data-testid={`${testid}-button`} onClick={submit} disabled={loading} className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
        <span className="ml-1.5">{loading ? "İşleniyor..." : "Yükle ve Güncelle"}</span>
      </Button>

      {result && (
        <div data-testid={`${testid}-result`} className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300">
          <div className="mb-1 flex items-center gap-1.5 font-semibold"><CheckCircle2 className="h-4 w-4" /> Güncellendi</div>
          {result}
        </div>
      )}
    </div>
  );
};

export default function DataUpdate() {
  const [invRes, setInvRes] = useState("");
  const [furRes, setFurRes] = useState("");
  const [bankRes, setBankRes] = useState("");
  const [logs, setLogs] = useState([]);

  const loadLogs = async () => setLogs(await api.importLogs());
  useEffect(() => {
    loadLogs();
  }, []);

  const uploadInvoices = (setRes) => async (fd) => {
    const r = await api.importInvoices(fd);
    const parts = [];
    if (r.new_invoices || r.parsed) parts.push(`${r.new_invoices} yeni fatura`);
    if (r.statement_rows) {
      const who = [];
      if (r.statement_customers) who.push(`${r.statement_customers} müşteri`);
      if (r.statement_suppliers) who.push(`${r.statement_suppliers} tedarikçi`);
      parts.push(`${r.statement_rows} ekstre satırı · ${who.join(" + ")} bakiyesi güncellendi`);
    }
    if (r.new_contacts) parts.push(`${r.new_contacts} yeni cari`);
    if (r.stock_items_touched) parts.push(`${r.stock_items_touched} stok kalemi`);
    const summary = (r.statement_summary || []).map(
      (s) => `${s.name} → ${s.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei (${s.new_rows} satır)`
    );
    setRes(
      <>
        {parts.length ? parts.join(" · ") : "İşlenecek yeni kayıt bulunamadı"}
        {summary.length > 0 && (
          <ul className="mt-2 space-y-0.5 border-t border-emerald-500/20 pt-2">
            {summary.map((line, i) => (
              <li key={i} data-testid="statement-summary-row" className="font-mono text-[11px]">{line}</li>
            ))}
          </ul>
        )}
      </>
    );
    toast.success(r.statement_rows ? `${r.statement_rows} ekstre satırı işlendi` : `${r.new_invoices} yeni fatura işlendi`);
    loadLogs();
  };

  const uploadBank = async (fd) => {
    const r = await api.importBank(fd);
    let msg = `${r.new_bank_tx} yeni banka hareketi · ${r.reconciled_in} tahsilat + ${r.reconciled_out} ödeme cari ile eşleşti`;
    if (r.notes_added) msg += ` · ${r.notes_added} cari kartına "tahsil edildi/ödendi" notu düşüldü`;
    if (r.capped_advances) msg += ` · ${r.capped_advances} carinin fazla tahsilatı avans olarak işaretlendi`;
    if (r.pending_suggestions) msg += ` · ${r.pending_suggestions} olası eşleşme onayınızı bekliyor (Ödeme/Tahsilat → Eşleşmeyen)`;
    msg += ` · Hesaplar: ${r.accounts.join(", ")}`;
    setBankRes(msg);
    if (r.pending_suggestions) toast.warning(`${r.pending_suggestions} olası eşleşme onayınızı bekliyor — Ödeme/Tahsilat sayfasından onaylayın`);
    toast.success(`${r.new_bank_tx} yeni hareket işlendi`);
    loadLogs();
  };

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title="Veri Güncelle"
        subtitle="Fatura ve banka dökümlerini yükleyin; sistem otomatik olarak güncellenir (mükerrer kayıtlar atlanır)"
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card
          testid="import-invoices"
          icon={ArrowDownRight}
          tone="bg-emerald-500/15 text-emerald-400"
          title="Client Faturaları (Satış)"
          desc="Kestiğiniz satış faturalarının XML/ZIP arşivi (gelir + cari + stok düşüşü) veya cari bakiye ekstresi (Evolutie sold client — .xls/.xlsx: fatura + tahsilat + kesin bakiye)."
          accept=".zip,.xls,.xlsx"
          multiple
          onUpload={uploadInvoices(setInvRes)}
          result={invRes}
        />
        <Card
          testid="import-furnizori"
          icon={ArrowUpRight}
          tone="bg-rose-500/15 text-rose-400"
          title="Furnizör Faturaları (Alış)"
          desc="Size gelen alış faturalarının XML/ZIP arşivi (gider + cari + stok artışı) veya tedarikçi ödeme durumu ekstresi (Situatie plati furnizori — .xls/.xlsx: açık faturalar + kesin borç bakiyesi)."
          accept=".zip,.xls,.xlsx"
          multiple
          onUpload={uploadInvoices(setFurRes)}
          result={furRes}
        />
        <Card
          testid="import-bank"
          icon={FileSpreadsheet}
          tone="bg-blue-500/15 text-blue-400"
          title="Banka Ekstresi (CSV)"
          desc="GarantiBank detaylı hesap ekstresi (RON/EUR/USD). Nakit akışı + otomatik cari mutabakat."
          accept=".csv"
          multiple
          onUpload={uploadBank}
          result={bankRes}
        />
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">
        <div className="flex items-center gap-2 font-medium text-foreground"><FileArchive className="h-4 w-4" /> Nasıl çalışır?</div>
        <ul className="mt-2 list-inside list-disc space-y-1">
          <li>Client/Furnizör ayrımı fatura içindeki firma vergi numarasından <b>otomatik</b> algılanır; her iki fatura kutusunu da kullanabilirsiniz.</li>
          <li>Aynı fatura/hareket tekrar yüklense bile <b>mükerrer kayıt eklenmez</b>, sadece yeni olanlar işlenir.</li>
          <li>Satış kutusuna <b>müşteri bakiye ekstresi (Excel — Evolutie sold client)</b>, Alış kutusuna <b>tedarikçi ödeme durumu (Excel — Situatie plati furnizori)</b> yükleyebilirsiniz: faturalar + tahsilat/borç işlenir ve carinin <b>kesin bakiyesi</b> yazılır. Format dosyadan <b>otomatik algılanır</b>; XML'de var olan faturalar atlanmaz, ayrı kaynakta tutulur (çift sayım olmaz).</li>
          <li>Banka girişi bir müşteriden geldiyse ilgili cari <b>alacağından düşülür</b>; tedarikçiye giden ödeme <b>borçtan düşülür</b>.</li>
          <li>SRL/SA/SNC içermeyen müşteriler <b>online satış (şahıs)</b> olarak sınıflandırılır.</li>
        </ul>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-foreground"><History className="h-[18px] w-[18px]" /></div>
          <h3 className="font-display text-lg font-medium">İçe Aktarma Günlüğü</h3>
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Tarih</th>
                <th className="px-4 py-3 font-semibold">Tür</th>
                <th className="px-4 py-3 text-right font-semibold">Yeni Kayıt</th>
                <th className="hidden px-4 py-3 font-semibold md:table-cell">Detay</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Henüz yükleme yapılmadı</td></tr>}
              {logs.map((l, i) => (
                <tr key={i} data-testid="import-log-row" className="border-b border-border/50">
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{new Date(l.date).toLocaleString("tr-TR")}</td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${l.type === "Banka" ? "bg-blue-500/15 text-blue-300" : "bg-emerald-500/15 text-emerald-300"}`}>{l.type}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold">{l.new_count.toLocaleString("tr-TR")}</td>
                  <td className="hidden px-4 py-2.5 text-xs text-muted-foreground md:table-cell">{l.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
